import {useMemo} from 'react';

import {aggregateUserAccess, ChipKind, initials, nameFromEmail, TenantChip} from '../lib/aggregate';
import {GrantsResponse} from '../lib/api';
import {kindHeading} from '../lib/chip-style';

import {AccessChip} from './AccessChip';
import {Spinner} from './Spinner';

interface Props {
    /** Current user's email. */
    email: string;
    data: GrantsResponse | undefined;
    loading: boolean;
    error: string | undefined;
    twingateDown: boolean;
    onReload: () => void;
}

/**
 * User-role replacement for the full GrantsPage. Two tables — DataAccess and
 * Impersonation — each shaped exactly like the admin's per-kind GrantsPage tab,
 * but filtered to a single user. Both tables always render, even when empty,
 * so the user sees both possible request targets even before they have any
 * access at all.
 *
 * Reuses `aggregateUserAccess` so the chip merge / expiry logic is identical
 * to what admins see for the same user.
 */
export function MyGrantsPage({email, data, loading, error, twingateDown, onReload}: Props) {
    const dataAccessChips = useMemo(() => chipsFor(data, email, 'data-access'), [data, email]);
    const impAiChips = useMemo(() => chipsFor(data, email, 'imp-ai'), [data, email]);
    const impersonationChips = useMemo(() => chipsFor(data, email, 'imp-decisions'), [data, email]);

    if (twingateDown) {
        return (
            <main className="page">
                <div className="twingate-prompt">
                    <h2>Подключите Twingate</h2>
                    <p>API недоступен — подключись и нажми Reload.</p>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onReload}
                        disabled={loading}
                    >
                        Reload
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="page">
            <div className="page-head">
                <h1>My Grants</h1>
                <div className="spacer" />
                <button type="button" className="btn-reload" onClick={onReload} disabled={loading}>
                    {loading ? 'Loading…' : 'Reload'}
                </button>
            </div>
            <div className="page-subhead">{email}</div>

            {loading && !data ? (
                <Spinner label="Loading grants…" />
            ) : error ? (
                <div className="card">
                    <div className="empty">
                        <h3>Failed to load</h3>
                        <div>{error}</div>
                    </div>
                </div>
            ) : (
                <>
                    <KindTable
                        title={kindHeading('imp-ai')}
                        email={email}
                        chips={impAiChips}
                        emptyLabel="No Ennabl AI impersonation — use Request access to ask for some."
                    />
                    <KindTable
                        title={kindHeading('data-access')}
                        email={email}
                        chips={dataAccessChips}
                        emptyLabel="No DataAccess grants — use Request access to ask for some."
                    />
                    <KindTable
                        title={kindHeading('imp-decisions')}
                        email={email}
                        chips={impersonationChips}
                        emptyLabel="No Decisions impersonation — use Request access to ask for some."
                    />
                </>
            )}
        </main>
    );
}

/**
 * Aggregates grants for the requested kind and returns the chips belonging to
 * `email`, or [] if the user has none. Mirrors what `aggregateUserAccess` does
 * for the admin's per-kind tabs, then drops everyone else's row.
 */
function chipsFor(
    data: GrantsResponse | undefined,
    email: string,
    kind: ChipKind
): TenantChip[] {
    if (!data) {
        return [];
    }
    const rows = aggregateUserAccess(data.grants, kind);
    return rows.find(r => r.email === email)?.chips ?? [];
}

function KindTable({
    title,
    email,
    chips,
    emptyLabel,
}: {
    title: string;
    email: string;
    chips: TenantChip[];
    emptyLabel: string;
}) {
    const name = nameFromEmail(email);
    return (
        <div className="my-grants-section">
            <h2>{title}</h2>
            <div className="card">
                <table className="tbl">
                    <thead>
                        <tr>
                            <th className="col-user">User</th>
                            <th>Access to tenants</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="col-user">
                                <div className="user-cell">
                                    <span className="avatar" aria-hidden>
                                        {initials(name, email)}
                                    </span>
                                    <div className="meta">
                                        <div className="name">{name || email}</div>
                                        <div className="email">{email}</div>
                                    </div>
                                </div>
                            </td>
                            <td>
                                {chips.length === 0 ? (
                                    <span className="my-grants-empty">{emptyLabel}</span>
                                ) : (
                                    <div className="access-chips">
                                        {chips.map(c => (
                                            <AccessChip key={`${c.resource}-${c.kind}`} chip={c} />
                                        ))}
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}
